
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Clock, Star, ArrowRight, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";

// Complete SERENITY Spa Service Menu with updated prices, services, and SEO-optimized alt text
const servicesData = [
  {
    id: 1,
    name: "التسويق الرقمي",
    category: "تسويق",
    description: "نساعدك في الوصول لجمهورك المستهدف عبر منصات التواصل الاجتماعي والإعلانات الرقمية",
    image_url: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
    alt_text: "التسويق الرقمي وإدارة الحملات الإعلانية",
  },
  {
    id: 2,
    name: "الهوية البصرية والعلامة التجارية",
    category: "تصميم",
    description: "نصمم لك هوية بصرية متكاملة تعكس قيم علامتك التجارية وتميزك عن المنافسين",
    image_url: "https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=600&q=80",
    alt_text: "تصميم الهوية البصرية والعلامة التجارية",
  },
  {
    id: 3,
    name: "الإنتاج الإعلامي والتصوير",
    category: "تصوير", 
    description: "إنتاج محتوى مرئي احترافي من تصوير فوتوغرافي وفيديو لتعزيز حضورك",
    image_url: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=600&q=80",
    alt_text: "الإنتاج الإعلامي والتصوير الاحترافي",
  },
  {
    id: 4,
    name: "الطباعة والإنتاج المطبعي",
    category: "طباعه",
    description: "خدمات طباعة عالية الجودة لجميع المواد الدعائية والمطبوعات التجارية",
    image_url: "https://images.unsplash.com/photo-1504270997636-07ddfbd48945?w=600&q=80",
    alt_text: "الطباعة والإنتاج المطبعي",
  },
  {
    id: 5,
    name: "تنظيم الفعاليات والإيفنتات",
    category: "تنظيم فعاليات",
    description: "نخطط وننظم فعالياتك من الألف إلى الياء لضمان نجاحها وتميزها",
    image_url: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80",
    alt_text: "تنظيم الفعاليات والمناسبات",
  },
  {
    id: 6,
    name: "الاستشارات التسويقية",
    category: "استشارات",
    description: "استشارات متخصصة لتطوير استراتيجيتك التسويقية وتحقيق أهدافك",
    image_url: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80",
    alt_text: "الاستشارات التسويقية والإعلانية",
  },
];

const categories = [
  { key: "الكل", name: "الكـل" },
  { key: "تسويق", name: "تسويـق" },
  { key: "تصميم", name: "تصميـم" },
  { key: "تنظيم فعاليات", name: "تنظيم فعاليات" },
  { key: "طباعه", name: "طباعة" },
  { key: "استشارات", name: "استشارات" },
  { key: "تصوير", name: "تصـويـر" }
];

export default function Services() {
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState("الكل");

  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  
  useEffect(() => {
    const categoryFromUrl = urlParams.get('category');
    if (categoryFromUrl && categories.some(cat => cat.key === categoryFromUrl)) {
      setActiveFilter(categoryFromUrl);
    }
  }, [urlParams]);

  const filteredServices = useMemo(() => {
    return activeFilter === "الكل" 
      ? servicesData 
      : servicesData.filter(service => service.category === activeFilter);
  }, [activeFilter]);

  const handleServiceBooking = useCallback((service) => {
    window.dispatchEvent(new CustomEvent('open-booking-modal-with-service', { 
      detail: { service } 
    }));
  }, []);

  return (
    <div className="pt-32 pb-24 bg-gradient-to-b from-[#e8e9eb] to-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          
          
          <h1 className="font-cairo font-bold text-4xl text-[#141d3c] mb-6 leading-tight">
خـدمـات زان          </h1>
          
          <p className="font-cairo text-xl text-gray-600 max-w-3xl mx-auto leading-[1.618]">
            نقدم لكم مجموعة متكاملة من الخدمات المصممة بعناية لتلبية احتياجاتكم وتسهيل أعمالكم بأعلى مستوى من الجودة والاحترافية.
          </p>
        </motion.div>

        {/* Category Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => setActiveFilter(category.key)}
              className={`font-cairo px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeFilter === category.key
                  ? 'bg-[#141d3c] text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-[#141d3c]/10 hover:text-[#141d3c] border border-gray-200'
              }`}
            >
              {category.name}
            </button>
          ))}
        </motion.div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-[clamp(1rem,2vw,2.5rem)]">
          {filteredServices.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.1,
                ease: "easeOut"
              }}
              whileHover={{ scale: 1.03, y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
              className="group bg-white rounded-3xl overflow-hidden shadow-lg will-change-transform"
            >
              {/* Service Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={service.image_url}
                  alt={service.alt_text}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
               

                {/* Duration Badge */}
                <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {service.duration}
                </div>
              </div>

              {/* Service Content */}
              <div className="p-6">
                <h3 className="font-cairo text-xl font-bold text-[#141d3c] mb-3 group-hover:text-[#141d3c] transition-colors duration-300">
                  {service.name}
                </h3>
                
                <p className="font-cairo text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                  {service.description}
                </p>

                {/* Rating */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-[#ee9805] fill-current" />
                  ))}
                  <span className="text-sm text-gray-500 ml-2">(4.9)</span>
                </div>
                
                <button 
                  onClick={() => handleServiceBooking(service)}
                  className="font-cairo w-full bg-[#141d3c]/10 text-[#141d3c] py-3 rounded-full font-medium hover:bg-[#141d3c] hover:text-white transition-all duration-300 group-hover:shadow-lg flex items-center justify-center gap-2"
                >
احجـز الآن                   <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-3xl p-12 shadow-lg border border-[#141d3c]/20">
            <h2 className="font-cairo text-[length:var(--font-h2)] font-bold text-[#141d3c] mb-4">
خلي فكرتك تتحول لواقع يلفت الأنظار             </h2>
            <p className="font-cairo text-gray-600 mb-8 max-w-2xl mx-auto leading-[1.618]">
              مع زان نبتكر لك حلول دعائية وإعلانية تصنع فرق وتخلي حضورك أقوى وأوضح.
            </p>
            <button 
              onClick={() => window.dispatchEvent(new CustomEvent('open-booking-modal'))}
              className="font-cairo bg-[#141d3c] text-white px-8 py-4 rounded-full font-medium hover:bg-[#ee9805] transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
ابـدأ معنا الآن            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
